/**
 * EntreprisePanel — Files PMS › Entreprise (Batch #14.314).
 * Boîte de réception des pièces envoyées par le client depuis la tuile « Entreprise » du portail :
 *   revenues = factures de revenus · expenses = dépenses + reçu photo · mileage = trajets.
 * Rien n'est saisi ici : on consulte la pièce, on la saisit soi-même en Tenue de livres, puis « Marquer traité ».
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, CheckCheck, BookOpenCheck, Inbox } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "./_shared";
import { ENT_KINDS } from "./_entreprise";
import EntrepriseInboxRow from "./EntrepriseInboxRow";

export default function EntreprisePanel({ kind, onUpdate }) {
  const cfg = ENT_KINDS[kind];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewed, setShowReviewed] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/entrepreneurs/inbox`, { params: { kind, include_reviewed: showReviewed }, headers: authH() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [kind, showReviewed]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onUpdate?.(); window.dispatchEvent(new CustomEvent("badges:refresh")); };

  const markAll = async (masterId) => {
    setBusy(masterId);
    try {
      const r = await axios.post(`${API}/admin/entrepreneurs/${masterId}/mark-reviewed`, {}, { params: { kind }, headers: authH() });
      toast.success(`${r.data.marked} pièce(s) marquée(s) traitée(s)`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(null); }
  };

  const items = data?.items || [];
  const groups = Object.values(items.reduce((acc, it) => {
    const g = acc[it.master_id] || (acc[it.master_id] = { master_id: it.master_id, name: it._master_name, number: it._client_number, items: [], total: 0 });
    g.items.push(it); g.total += Number(cfg.amount(it)) || 0;
    return acc;
  }, {})).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="p-4 sm:p-6" data-testid={`admin-pms-ent-panel-${kind}`}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-slate-800 inline-flex items-center gap-2"><cfg.icon className={`w-5 h-5 ${cfg.text}`} /> {cfg.title}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{cfg.hint}</p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={showReviewed} onChange={(e) => setShowReviewed(e.target.checked)} className="accent-teal-700" data-testid="ent-inbox-show-reviewed" /> Afficher les pièces traitées (12 mois)
        </label>
      </div>

      {loading && !data ? <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" /></div>
        : items.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200" data-testid="ent-inbox-empty">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">{showReviewed ? "Aucune pièce reçue sur les 12 derniers mois." : "Aucune pièce à traiter — tout est à jour."}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const pending = g.items.filter((it) => it.reviewed === false).length;
              return (
                <div key={g.master_id} className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={`ent-inbox-group-${g.master_id}`}>
                  <div className={`flex items-center gap-3 p-3 ${cfg.headBg} border-b border-slate-100 flex-wrap`}>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-slate-800 text-sm">{g.number && <span className="font-mono text-teal-700 mr-1.5">{g.number}</span>}{g.name}</span>
                      <span className="text-xs text-slate-500 ml-2">{g.items.length} pièce(s){pending > 0 && <> · <b className="text-rose-700" data-testid={`ent-inbox-group-pending-${g.master_id}`}>{pending} à traiter</b></>} · <b>{cfg.fmtTotal(g.total)}</b></span>
                    </div>
                    <Link to={cfg.booksLink(g.master_id)} className="px-2 py-1 rounded text-xs border border-teal-300 bg-white text-teal-800 hover:bg-teal-50 inline-flex items-center gap-1" data-testid={`ent-inbox-open-books-${g.master_id}`}>
                      <BookOpenCheck className="w-3 h-3" /> {cfg.booksLabel}
                    </Link>
                    {pending > 0 && (
                      <button onClick={() => markAll(g.master_id)} disabled={busy === g.master_id}
                        className="px-2 py-1 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`ent-inbox-mark-all-${g.master_id}`}>
                        {busy === g.master_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className="w-3 h-3" />} Tout marquer traité
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {g.items.map((it) => <EntrepriseInboxRow key={it.id} kind={kind} item={it} onChanged={refresh} />)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
